// VFX 모듈 12종 라이브러리(PRD 3A.3, D1d)의 공통 계약. 1차 6종: 아크·트레일·링·균열 데칼·투사체·화면 효과.
// 2차 6종(c007): 빔·장판·기둥·폭발 플립북·흡입 소용돌이·오라.
// 모든 모듈은 같은 인터페이스(spawn(params) / update(dt) / clear / dispose)와 같은 파라미터 구조(FxParams)를 쓰고,
// 색·스케일·길이만 바꿔 스킬마다 변형한다. 스킬을 개별 제작하지 않는다.
// 규약: 시간은 초, 길이는 m, 각도는 rad. facing 방향 벡터는 게임과 같이 (sin yaw, cos yaw).
// 프레임 루프에서 새 객체를 만들지 않는다: 슬롯·지오메트리·머티리얼은 생성 때 한 번만 만들고 spawn은 슬롯 값만 덮어쓴다.
import type * as THREE from 'three/webgpu';

/** 모듈 이름 */
export type ModuleKind =
  | 'arc' | 'trail' | 'ring' | 'crack' | 'projectile' | 'screen'
  | 'beam' | 'zone' | 'pillar' | 'blast' | 'vortex' | 'aura'
  | 'streak';

/**
 * 전투 액션 VFX(2026-10-27, src/vfx/action)의 배치 모듈 이름. 스킬 큐 표(cueMap)·13종 라이브러리(VfxModules)에는 들어가지 않고
 * ActionVfx가 직접 띄운다(같은 BatchModule 바탕, 모듈당 드로우콜 1).
 */
export type ActionModuleKind = 'slashRing' | 'hitBurst';

/** 1차 6종 */
export const MODULE_KINDS_1: readonly ModuleKind[] = ['arc', 'trail', 'ring', 'crack', 'projectile', 'screen'];
/** 2차 6종 */
export const MODULE_KINDS_2: readonly ModuleKind[] = ['beam', 'zone', 'pillar', 'blast', 'vortex', 'aura'];
/** 3차(c017 장비 연출): 유도 줄기(드롭이 영웅에게 빨려 드는 나선·접히는 빛기둥, 처치 효과 알갱이) */
export const MODULE_KINDS_3: readonly ModuleKind[] = ['streak'];
/** 13종 전부(생성 순서) */
export const MODULE_KINDS: readonly ModuleKind[] = [...MODULE_KINDS_1, ...MODULE_KINDS_2, ...MODULE_KINDS_3];

/**
 * 대상 추적 핸들(오라). 호출하는 쪽이 객체 하나를 만들어 두고 매 프레임 x,y,z를 갱신한다(할당 없음).
 * active를 false로 바꾸면 따라다니던 효과가 0.2초 안에 사라진다(대상이 죽거나 사라졌을 때).
 */
export interface FxTarget { x: number; y: number; z: number; yaw: number; active: boolean }
export function makeTarget(x = 0, y = 0, z = 0, yaw = 0): FxTarget { return { x, y, z, yaw, active: true }; }

/**
 * 모듈 공통 파라미터. 모듈마다 쓰는 칸이 다르다(사용처는 각 모듈 파일 머리 주석과 README 표).
 * 호출하는 쪽은 객체 하나를 재사용하며 fillParams로 채운다.
 */
export interface FxParams {
  /** 기준점(m). 보통 영웅 위치 */
  x: number; y: number; z: number;
  /** 바라보는 방향(rad). 방향 벡터 (sin, cos) */
  yaw: number;
  /** 주색, 부색(핵·가장자리) 0xRRGGBB */
  color: number; color2: number;
  /** 길이·폭에 곱하는 전체 배율 */
  scale: number;
  /** 길이(m): 아크 반경 / 트레일·투사체 이동 거리 / 링 최종 반경 / 균열 앞쪽 길이(반경) / 빔 길이 / 장판 반경 / 기둥 높이 / 폭발 지름 / 소용돌이 바깥 반경 / 오라 높이 */
  length: number;
  /** 폭(m): 띠 두께 / 기둥·오라 반지름 / 소용돌이 띠 두께 */
  width: number;
  /** 수명(초) */
  life: number;
  /** 밝기 배율(HDR). 화면 효과는 최대 불투명도 */
  intensity: number;
  /** 노이즈 위상(모양 변형) */
  seed: number;
  /** 호·트레일 평면 기울기: 0 = 바닥과 평행, PI/2 = 세로 */
  roll: number;
  /** 호 시작 각 / 쓸어 지나가는 각(부호 = 방향). 장판 부채꼴 전체 각, 소용돌이 나선이 감기는 전체 각(부호 = 감기는 방향)으로도 쓴다 */
  start: number; sweep: number;
  /** 꼬리 길이: 트레일 = 초, 투사체 = m */
  tail: number;
  /** 시작 크기 비율(링 반경·균열 도달 시작, 소용돌이 안쪽 끝 반경 비율) */
  from: number;
  /** 변형 번호: 트레일 0 직선 / 1 호, 균열 0 부채꼴 / 1 방사, 화면 0 가장자리 비네트 / 1 전체 색조, 장판 0 원 / 1 부채꼴(sweep), 폭발 0 불+연기 / 1 연기 위주 */
  variant: number;
  /** 기준점에서 yaw 방향으로 앞으로 옮기는 거리(m) */
  ahead: number;
  /** 이미 흐른 시간(초): 이벤트가 늦게 도착했을 때 따라잡는다 */
  age0: number;
}

/** 프리셋: 바꿀 값만 적는다(나머지는 기본값). yaw는 방향 오프셋, y는 높이 오프셋이다 */
export type FxPreset = Partial<FxParams>;

export interface FxModule {
  readonly kind: ModuleKind | ActionModuleKind;
  /** 장면에 한 번 붙이는 그룹 */
  readonly group: THREE.Object3D;
  /** 동시 활성 상한 */
  readonly capacity: number;
  /** 지금 살아 있는 수 */
  readonly activeCount: number;
  /** 지금 이 모듈이 쓰는 드로우콜(0 또는 1) */
  readonly drawCalls: number;
  /** 하나 띄운다. 가득 차면 가장 오래된 것을 대체한다. 거부(화면 효과 섬광 한도)면 false */
  spawn(p: Readonly<FxParams>): boolean;
  /** dt초만큼 진행한다(영웅 시계: 히트스톱·일시정지에 멈춘다) */
  update(dt: number): void;
  /** 모두 지운다(장면 전환) */
  clear(): void;
  /** 미리 컴파일용: 보이게 둔다 */
  prewarm(on: boolean): void;
  dispose(): void;
}

/** 모듈별 동시 활성 상한(모바일 예산, TECH 4장 드로우콜 150 이하 중 모듈 전체 합 ≤ 13: 모듈당 1) */
export const MODULE_CAPS: Readonly<Record<ModuleKind, number>> = {
  arc: 6,
  trail: 8,
  ring: 8,
  // C9: 4 → 6. 대지 가르기 내려찍기 1 + 균열 번짐 3(수명 1.2초)이 낙화·초승달 균열과 겹쳐도 가장 오래된 균열을 밀어내지 않게
  crack: 6,
  projectile: 8,
  screen: 3,
  beam: 4,
  zone: 4,
  pillar: 6,
  blast: 6,
  vortex: 3,
  // c015: 4 → 8. 기상 무적 오라가 5마리부터 가장 오래된 것을 밀어내 무적 표시가 조용히 사라졌다. 배치 모듈이라 드로우콜은 그대로 1(정점 8 × 19 × 4)
  aura: 8,
  // c017: 줍기 줄기(전설·세트는 두 가닥) + 접히는 기둥 + 처치 효과 알갱이. 배치 모듈이라 드로우콜 1(정점 38 × 12)
  streak: 12,
};

/** 모듈 하나가 쓸 수 있는 드로우콜 상한 */
export const MODULE_DRAWCALL_MAX = 2;

/** 60Hz 고정 틱 */
export const TICK_HZ = 60;

export const FX_DEFAULTS: Readonly<FxParams> = {
  x: 0, y: 0, z: 0, yaw: 0,
  color: 0xff2a3d, color2: 0xfff1d6,
  scale: 1, length: 3, width: 0.6, life: 0.4, intensity: 1.6, seed: 0,
  roll: 0, start: -1, sweep: 2, tail: 0.12, from: 0.3, variant: 0, ahead: 0, age0: 0,
};

const KEYS = [
  'y', 'yaw', 'color', 'color2', 'scale', 'length', 'width', 'life', 'intensity', 'seed',
  'roll', 'start', 'sweep', 'tail', 'from', 'variant', 'ahead', 'age0',
] as const satisfies readonly (keyof FxParams)[];

/** 기본값 사본(생성 때 한 번) */
export function makeParams(): FxParams {
  return { ...FX_DEFAULTS };
}

/**
 * dst를 프리셋으로 채운다(할당 없음). 위치 x,z와 방향은 호출하는 쪽이 정하고,
 * 프리셋의 y는 높이 오프셋, yaw는 방향 오프셋으로 더한다.
 */
export function fillParams(dst: FxParams, preset: FxPreset, x: number, y: number, z: number, yaw: number): FxParams {
  for (let i = 0; i < KEYS.length; i++) {
    const k = KEYS[i];
    const v = preset[k];
    dst[k] = v === undefined ? FX_DEFAULTS[k] : v;
  }
  dst.x = x;
  dst.z = z;
  dst.y = y + (preset.y ?? 0);
  dst.yaw = yaw + (preset.yaw ?? 0);
  return dst;
}
